import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { CheckCircle2, XCircle, ArrowLeft, Trophy, AlertTriangle, RotateCcw } from 'lucide-react';

// 10-question mastery quiz. 100% required to pass.
// Wrong answers are shown with per-question remediation before the retake.
export default function ApexQuiz() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [passThreshold, setPassThreshold] = useState(100);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setResult(null);
    setAnswers({});
    setErr('');
    try {
      const r = await api.get('/apex/quiz');
      setQuestions(r.data.questions || []);
      setPassThreshold(r.data.pass_threshold_pct || 100);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load quiz');
    }
  };

  const setAns = (qid, idx) => setAnswers(a => ({ ...a, [qid]: idx }));
  const allAnswered = questions.length > 0 && questions.every(q => answers[q.id] != null);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post('/apex/quiz/submit', { answers });
      setResult(r.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- RESULT VIEW ----
  if (result) {
    const passed = result.passed;
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="apex-quiz-result-page">
        <div className="max-w-3xl mx-auto px-6 py-8">
          <button onClick={() => navigate('/apex/course')}
                  className="text-sm text-gray-400 hover:text-white flex items-center gap-2 mb-6"
                  data-testid="apex-quiz-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back to Course
          </button>

          <Card className={`border-2 ${passed ? 'border-green-500/60 bg-green-500/5' : 'border-orange-500/50 bg-orange-500/5'} mb-6`}>
            <CardContent className="p-6 text-center">
              {passed ? (
                <>
                  <Trophy className="w-12 h-12 text-green-400 mx-auto mb-3" />
                  <div className="text-3xl font-black mb-1" data-testid="apex-quiz-result-score">{result.score_pct}%</div>
                  <div className="text-lg font-bold text-green-400 mb-1" data-testid="apex-quiz-result-passed">MASTERY UNLOCKED</div>
                  <p className="text-sm text-gray-400 mb-4">Verified Marketable Athlete badge earned. Modules 3 &amp; 4 and the Recruiting Dossier PDF unlock in Phase 2.</p>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-12 h-12 text-orange-400 mx-auto mb-3" />
                  <div className="text-3xl font-black mb-1" data-testid="apex-quiz-result-score">{result.score_pct}%</div>
                  <div className="text-lg font-bold text-orange-400 mb-1" data-testid="apex-quiz-result-not-passed">
                    {result.correct}/{result.total} correct — {passThreshold}% required
                  </div>
                  <p className="text-sm text-gray-400 mb-4">Review each miss below, then retake. You have unlimited attempts.</p>
                </>
              )}
            </CardContent>
          </Card>

          {/* Per-question review with remediation on misses */}
          <div className="space-y-3">
            {result.per_question.map((pq, i) => {
              const q = questions.find(qq => qq.id === pq.id);
              const correctText = q?.choices?.[pq.correct_answer_index];
              const yourText = q?.choices?.[pq.your_answer_index];
              return (
                <Card key={pq.id} className={`border ${pq.correct ? 'border-green-500/30 bg-black/40' : 'border-red-500/30 bg-red-500/5'}`}
                      data-testid={`apex-quiz-review-${pq.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      {pq.correct
                        ? <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        : <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />}
                      <div className="flex-1">
                        <div className="text-xs text-gray-500 uppercase tracking-widest mb-1">Question {i + 1}</div>
                        <p className="font-semibold mb-3">{q?.prompt}</p>
                        {!pq.correct && (
                          <>
                            <div className="text-xs text-red-400 mb-1">You chose:</div>
                            <div className="text-sm text-gray-300 mb-2">{yourText}</div>
                            <div className="text-xs text-green-400 mb-1">Correct answer:</div>
                            <div className="text-sm text-gray-300 mb-3">{correctText}</div>
                            <div className="p-3 rounded bg-black/60 border border-orange-500/30">
                              <div className="text-[10px] text-orange-400 uppercase tracking-widest mb-1 font-bold">Remediation</div>
                              <p className="text-sm text-gray-300">{pq.remediation}</p>
                            </div>
                          </>
                        )}
                        {pq.correct && (
                          <div className="text-sm text-green-400/80">You chose: {yourText} ✓</div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="mt-6 flex gap-3">
            {!passed && (
              <Button onClick={load} className="bg-orange-500 hover:bg-orange-600 text-black font-bold"
                      data-testid="apex-quiz-retake-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> Retake Quiz
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate('/apex/course')}
                    className="border-gray-700 text-white hover:bg-gray-800"
                    data-testid="apex-quiz-done-btn">
              {passed ? 'Continue' : 'Back to Course'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---- TAKING VIEW ----
  const answeredCount = Object.keys(answers).length;
  const pct = questions.length ? Math.round((answeredCount / questions.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="apex-quiz-page">
      <div className="sticky top-0 bg-[#0a0a0a]/95 backdrop-blur border-b border-gray-800/60 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <button onClick={() => navigate('/apex/course')}
                    className="text-xs text-gray-400 hover:text-white flex items-center gap-2"
                    data-testid="apex-quiz-abort-btn">
              <ArrowLeft className="w-4 h-4" /> Cancel
            </button>
            <div className="text-xs text-gray-400" data-testid="apex-quiz-progress-label">
              {answeredCount}/{questions.length} answered · <span className="text-orange-400">{passThreshold}% to pass</span>
            </div>
          </div>
          <Progress value={pct} className="h-1" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-6">
        {err && <p className="text-red-400 text-sm mb-4" data-testid="apex-quiz-error">{err}</p>}

        <div className="space-y-4">
          {questions.map((q, i) => (
            <Card key={q.id} className="bg-[#111] border border-gray-800/60" data-testid={`apex-quiz-q-${q.id}`}>
              <CardContent className="p-5">
                <div className="text-xs text-gray-500 uppercase tracking-widest mb-2">Question {i + 1}</div>
                <p className="font-semibold mb-4">{q.prompt}</p>
                <div className="space-y-2">
                  {q.choices.map((c, ci) => {
                    const chosen = answers[q.id] === ci;
                    return (
                      <button
                        key={ci}
                        type="button"
                        onClick={() => setAns(q.id, ci)}
                        className={`w-full text-left p-3 rounded border transition-colors ${
                          chosen
                            ? 'border-orange-500 bg-orange-500/10'
                            : 'border-gray-800 hover:border-gray-600 bg-black/30'
                        }`}
                        data-testid={`apex-quiz-choice-${q.id}-${ci}`}
                      >
                        <span className={`inline-block w-6 h-6 rounded-full mr-3 text-xs font-bold flex-shrink-0 ${
                          chosen ? 'bg-orange-500 text-black' : 'bg-gray-800 text-gray-400'
                        } text-center leading-6`}>
                          {String.fromCharCode(65 + ci)}
                        </span>
                        <span className="text-sm text-gray-200">{c}</span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-6">
          <Button
            disabled={!allAnswered || submitting}
            onClick={submit}
            className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold text-base h-12"
            data-testid="apex-quiz-submit-btn"
          >
            {submitting ? 'Grading…' : allAnswered ? 'Submit Quiz' : `Answer ${questions.length - answeredCount} more to submit`}
          </Button>
        </div>
      </div>
    </div>
  );
}
